import { Box } from '@mui/material';
import { colorSet, typography } from '@theme';
import { useCallback, useRef, useId, useState, useEffect } from 'react';

interface IAutosizeTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  defaultRow?: number;
  placeholder?: string;
  boxstyle?: React.CSSProperties;
  style?: React.CSSProperties;
}

export const AutosizeTextarea = (props: IAutosizeTextareaProps) => {
  const { defaultRow = 1, placeholder, boxstyle, style, onChange, id, name, ...textareaProps } = props;

  const lineHeight = 20;
  const padding = 24;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const defaultId = useId();
  const [textareaHeight, setTextareaHeight] = useState(lineHeight * defaultRow);

  const calculateHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return lineHeight * defaultRow;

    const minHeight = lineHeight * defaultRow;

    // 내용이 없으면 최소 높이
    if (textarea.value.length === 0) {
      return minHeight;
    }

    // 높이를 minHeight로 일시적으로 설정하여 scrollHeight 정확히 측정
    const prevHeight = textarea.style.height;
    textarea.style.height = `${minHeight}px`;
    const scrollHeight = textarea.scrollHeight;
    textarea.style.height = prevHeight;

    // scrollHeight가 minHeight보다 크면 실제 내용이 넘친 것
    if (scrollHeight > minHeight) {
      return scrollHeight;
    }

    return minHeight;
  }, [defaultRow, lineHeight]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newHeight = calculateHeight();
      setTextareaHeight(newHeight);
      if (onChange) {
        onChange(e);
      }
    },
    [onChange, calculateHeight]
  );

  // defaultRow 변경 감지하여 높이 재계산
  useEffect(() => {
    const newHeight = calculateHeight();
    setTextareaHeight(newHeight);
  }, [defaultRow, calculateHeight]);

  return (
    <Box
      sx={{
        width: '100%',
        height: textareaHeight + padding,
        padding: '12px 16px',
        borderRadius: '5px',
        border: `1px solid ${colorSet.gray300}`,
        backgroundColor: colorSet.white,
        '&:hover': {
          border: `1px solid ${colorSet.primaryMain}`
        },
        '&:focus-within': {
          border: `1px solid ${colorSet.gray600}`,
          boxShadow: 'none',
          outline: 'none'
        },
        ...boxstyle
      }}
    >
      <textarea
        ref={textareaRef}
        {...textareaProps}
        id={id || defaultId}
        name={name}
        placeholder={placeholder}
        style={{
          ...typography.body1,
          lineHeight: `${lineHeight}px`,
          width: '100%',
          height: textareaHeight,
          padding: 0,
          margin: 0,
          outline: 'none',
          resize: 'none',
          border: 'none',
          overflow: 'hidden',
          boxSizing: 'border-box',
          ...style
        }}
        onChange={handleChange}
      />
    </Box>
  );
};
